import React, { useEffect, useState, useRef } from "react";
import { useStaticQuery, graphql, Link } from "gatsby";
import { css } from "@emotion/react";
import objectFitImages from 'object-fit-images'
import { colors } from "../assets/styles/variables";
import { ISOStringTranslator } from "../utils/dateFormat"
import { Base64 } from "../utils/base64"
import { getTime } from "../utils/getTime";

interface Props {
  ownSlug?: string;
  isIncludedOwnSlug?: boolean;
}

/**
 * GraphQLから取得した後、型を確定させたオブジェクト
 */
interface EffectiveItem {
  titleEditionSlug: string,
  episodeSlug: string,
  titleId: string,
  publishFrom: number,
  publishTo: number,
  ogImageIcon: string | null,
  ogImageLarge: string
}

/**
 * 最終的に出力するオブジェクト
 */
interface FormattedItem {
  key: string;
  title: string;
  url: string;
  thumbnail: string;
}

/**
 * nullや空文字を除外し、型を確定させたオブジェクトを抽出
 * @param array 
 */
const pickEffective = (array: GatsbyTypes.RecommendedComicsQuery["allDynamoDbData"]["nodes"]): EffectiveItem[] => {
  const result: EffectiveItem[] = []
  const isEmpty = (value?: string | null) => !value || value === " "

  for (const item of array) {
    // og_image_icon以外で、nullや空文字の値が1つでもあったら対象から外す
    const isInvalid = Object.entries(item).some(([key, value]) => {
      if (key !== "og_image_icon") return isEmpty(value)
    })
    if (!isInvalid) result.push({
      titleEditionSlug: item.title_edition_slug as string,
      episodeSlug: item.episode_slug as string,
      titleId: item.title_id as string,
      publishFrom: Number(ISOStringTranslator(item.publish_from)),
      publishTo: Number(ISOStringTranslator(item.publish_to)),
      ogImageIcon: isEmpty(item.og_image_icon) ? null : item.og_image_icon as string,
      ogImageLarge: item.og_image_large as string
    })
  }
  return result
}

/**
 * 公開中のエピソードを抽出
 * @param array 
 */
const pickOpen = async (array: EffectiveItem[]): Promise<EffectiveItem[]> => {
  const currentTime = await getTime();
  return array.filter(item => (
    item.publishTo > currentTime &&
    item.publishFrom < currentTime
  ))
}

/**
 * 同一のtitle_edition_slugのうち、publish_fromが一番新しいエピソードのみ抽出
 * @param array 
 */
const pickUnique = (array: EffectiveItem[]): EffectiveItem[] => {
  const result: EffectiveItem[] = [];
  for (const current of array) {
    let isSame = false;
    for (let index = 0, length = result.length; index < length; index++) {
      const existing = result[index];
      if (existing.titleEditionSlug === current.titleEditionSlug) {
        if (existing.publishFrom < current.publishFrom) {
          result.splice(index, 1, current);
        }
        isSame = true;
        break;
      }
    }
    if (!isSame) {
      result.push(current);
    }
  }
  return result
}

/**
 * 自分自身を除く
 * @param array 
 * @param ownSlug 自分自身のtitleEditionSlug
 */
const pickOthers = (array: EffectiveItem[], ownSlug: string): EffectiveItem[] => {
  const ownSlugEncoded = Base64.encode(ownSlug);
  return array.filter(item => item.titleEditionSlug !== ownSlugEncoded)
}

/**
 * ランダムで3つ抽出。
 * ownSlugが渡された場合、自分自身を優先的に抽出
 * @param array 
 * @param ownSlug 自分自身のtitleEditionSlug
 */
const pickRandomly = (array: EffectiveItem[], ownSlug?: string): EffectiveItem[] => {
  let num = 3;
  const result: EffectiveItem[] = [];

  if (ownSlug) {
    const ownSlugEncoded = Base64.encode(ownSlug);
    for (const item of array) {
      if (item.titleEditionSlug === ownSlugEncoded) {
        result.push(item);
        num--;
      }
    }
  }

  for (let index = 0; index < num; index++) {
    const targetIndex = Math.floor(Math.random() * array.length);
    result.push(array[targetIndex]);
    array.splice(targetIndex, 1);
  }
  return result
}

/**
 * 出力用に整形
 * @param array 
 */
const format = (array: EffectiveItem[]): FormattedItem[] => array.map((item) => ({
  key: item.titleEditionSlug,
  title: Base64.decode(item.titleId),
  url: `/${Base64.decode(item.titleEditionSlug)}/${Base64.decode(item.episodeSlug)}`,
  thumbnail: item.ogImageIcon ? Base64.decode(item.ogImageIcon) : Base64.decode(item.ogImageLarge)
}))

const recommended = css`
  max-width: 342px;
  width: 100%;
  margin: 18px auto 0;

  .heading {
    color: ${colors.white};
    font-size: 12px;
    font-weight: bold;
  }

  .list {
    margin-top: 16px;
    display: flex;
    justify-content: space-between;
    min-height: 174px; // レイアウトシフトを防ぐために設定。タイトル5行分の高さ。

    > li {
      width: 30%;
    }
  }

  .link {
    color: ${colors.white};
    text-decoration: none;
    display: block;
  }

  .title {
    font-size: 12px;
    margin-top: 8px;
  }

  .thumbnail {
    position: relative;
    display: block;

    &::before {
      content: "";
      display: block;
      padding-top: 100%;
    }

    > img {
      position: absolute;
      width: 100%;
      height: 100%;
      display: block;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      margin: auto;
      object-fit: cover;
      font-family: 'object-fit: cover;'; // for objectFitImages
    }
  }
`

const Recommended = (props: Props) => {
  const { ownSlug, isIncludedOwnSlug } = props;
  const [recommendedComics, setRecommendedComics] = useState<FormattedItem[]>([]);
  const thumbnailRef = useRef(null);
  const { allDynamoDbData } = useStaticQuery<GatsbyTypes.RecommendedComicsQuery>(graphql`
    query RecommendedComics {
      allDynamoDbData {
        nodes {
          title_edition_slug,
          episode_slug,
          title_id,
          publish_from,
          publish_to,
          og_image_icon,
          og_image_large
        }
      }
    }
  `);

  useEffect(() => {
    const setFormattedItems = async () => {
      if (ownSlug) {
        if (isIncludedOwnSlug) {
          setRecommendedComics(format(pickRandomly(pickUnique(await pickOpen(pickEffective(allDynamoDbData.nodes))), ownSlug)));
        } else {
          setRecommendedComics(format(pickRandomly(pickOthers(pickUnique(await pickOpen(pickEffective(allDynamoDbData.nodes))), ownSlug))));
        }
      } else {
        setRecommendedComics(format(pickRandomly(pickUnique(await pickOpen(pickEffective(allDynamoDbData.nodes))))));
      }
    }
    setFormattedItems();
  }, [allDynamoDbData])

  // IEにobject-fitを対応させる
  useEffect(() => {
    objectFitImages(thumbnailRef.current)
  }, [thumbnailRef])

  return (
    <section css={recommended}>
      <h2 className="heading">こちらもオススメ！</h2>
      <ul className="list">
        {recommendedComics.map(item => (
          <li key={item.key}>
            <Link
              className="link"
              to={item.url}
            >
              <span className="thumbnail">
                <img
                  src={item.thumbnail}
                  alt=""
                  ref={thumbnailRef}
                />
              </span>
              <p className="title">{item.title}</p>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
};

export default Recommended;
